import { useCallback } from 'react';
import { InputNumber, Select } from 'antd';
import { isEmpty, sortBy } from 'lodash';

import { Form, WrapperSelect } from 'common/components';
import { OverlayInputWrapper } from 'common/components/overlay';
import {
  checkActiveStatus,
  isUnitPackageLevel,
} from 'common/components/product-add/components/utils/validation';

const styles = {
  numericInput: {
    width: '100%',
  },
};

const formItemLayout = {
  labelCol: {
    xl: {
      span: 24,
    },
    xxl: {
      span: 10,
    },
  },
  wrapperCol: {
    xl: {
      span: 24,
    },
    xxl: {
      span: 14,
    },
  },
};

const { Option } = Select;

const MAX_NUMBER_INPUT_UPC = 9999999999999; // 14 digits

export const PackageWeightFieldset = (props) => {
  const {
    getOverlayProps,
    productBasicUOM,
    isProductOverlay,
    isFieldEditable,
    isVariableWeightsAndMeasures,
  } = props;

  const formInstance = Form.useFormInstance();

  const enumDimensionsProperties =
    productBasicUOM?.filter((type) => type.enumGroup === 'Dimensions') || [];

  const enumMassProperties =
    productBasicUOM?.filter((type) => type.enumGroup === 'Mass') || [];

  const selectionFilter = useCallback(
    (input, option) =>
      option.children?.toLowerCase().indexOf(input?.toLowerCase()) >= 0,
    []
  );
  const packageLevel = Form.useWatch('packageLevel');

  const isUnit = isUnitPackageLevel(packageLevel);
  const isActiveStatus = checkActiveStatus(Form.useWatch('status'));

  const handleGetFormError = (fieldName) => {
    return !isEmpty(formInstance.getFieldError(fieldName)); // []
  };

  const isRequiredDims = (fieldName) =>
    isUnit && isActiveStatus && handleGetFormError(fieldName);

  return (
    <>
      <Form.Item
        label='Packaged Width'
        name='width'
        rules={[
          {
            required: !isVariableWeightsAndMeasures && isRequiredDims('width'),
            message: 'Packaged Width is required',
          },
        ]}
        {...formItemLayout}
      >
        <OverlayInputWrapper {...getOverlayProps('width')}>
          <InputNumber
            style={styles.numericInput}
            min={0}
            max={MAX_NUMBER_INPUT_UPC}
            precision={3}
            disabled={isFieldEditable('width')}
          />
        </OverlayInputWrapper>
      </Form.Item>

      <Form.Item
        label='Packaged Height'
        name='height'
        rules={[
          {
            required: !isVariableWeightsAndMeasures && isRequiredDims('height'),
            message: 'Packaged Height is required',
          },
        ]}
        {...formItemLayout}
      >
        <OverlayInputWrapper {...getOverlayProps('height')}>
          <InputNumber
            style={styles.numericInput}
            min={0}
            max={MAX_NUMBER_INPUT_UPC}
            precision={3}
            disabled={isFieldEditable('height')}
          />
        </OverlayInputWrapper>
      </Form.Item>

      <Form.Item
        label='Packaged Depth'
        name='depth'
        rules={[
          {
            required: !isVariableWeightsAndMeasures && isRequiredDims('depth'),
            message: 'Packaged Depth is required',
          },
        ]}
        {...formItemLayout}
      >
        <OverlayInputWrapper {...getOverlayProps('depth')}>
          <InputNumber
            style={styles.numericInput}
            min={0}
            max={MAX_NUMBER_INPUT_UPC}
            precision={3}
            disabled={isFieldEditable('depth')}
          />
        </OverlayInputWrapper>
      </Form.Item>

      <Form.Item
        label='Packaged Weight'
        name='weight'
        rules={[
          {
            required: !isVariableWeightsAndMeasures && isRequiredDims('weight'),
            message: 'Packaged Weight is required',
          },
        ]}
        {...formItemLayout}
      >
        <OverlayInputWrapper {...getOverlayProps('weight')}>
          <InputNumber
            style={styles.numericInput}
            min={0}
            max={MAX_NUMBER_INPUT_UPC}
            precision={3}
            disabled={isVariableWeightsAndMeasures || isFieldEditable('weight')}
          />
        </OverlayInputWrapper>
      </Form.Item>

      <Form.Item
        label='Average Weight'
        name='averageWeight'
        {...formItemLayout}
      >
        <OverlayInputWrapper {...getOverlayProps('averageWeight')}>
          <InputNumber
            style={styles.numericInput}
            min={0}
            max={MAX_NUMBER_INPUT_UPC}
            disabled={!isVariableWeightsAndMeasures || isProductOverlay}
          />
        </OverlayInputWrapper>
      </Form.Item>

      <Form.Item
        label='Packaged Weight UOM'
        name='weightUOM'
        rules={[
          {
            required:
              !isVariableWeightsAndMeasures && isRequiredDims('weightUOM'),
            message: 'Packaged Weight UOM is required',
          },
        ]}
        {...formItemLayout}
      >
        <OverlayInputWrapper {...getOverlayProps('weightUOM')}>
          <WrapperSelect
            showSearch
            filterOption={selectionFilter}
            getPopupContainer={() => document.getElementById('package-field')}
            disabled={isFieldEditable('weightUOM')}
          >
            {[
              sortBy([...enumMassProperties], ['enumDisplayName']).map(
                (type, index) => {
                  return (
                    <Option value={type?.enumDisplayName} key={index}>
                      {type?.enumDisplayName}
                    </Option>
                  );
                }
              ),
            ]}
          </WrapperSelect>
        </OverlayInputWrapper>
      </Form.Item>

      <Form.Item
        label='Packaged Dimension UOM'
        name='dimensionUOM'
        rules={[
          {
            required:
              !isVariableWeightsAndMeasures && isRequiredDims('dimensionUOM'),
            message: 'Packaged Dimension UOM is required',
          },
        ]}
        {...formItemLayout}
      >
        <OverlayInputWrapper {...getOverlayProps('dimensionUOM')}>
          <WrapperSelect
            showSearch
            filterOption={selectionFilter}
            disabled={isFieldEditable('dimensionUOM')}
            getPopupContainer={() => document.getElementById('package-field')}
          >
            {[
              sortBy([...enumDimensionsProperties], ['enumDisplayName']).map(
                (type, index) => {
                  return (
                    <Option value={type?.enumDisplayName} key={index}>
                      {type?.enumDisplayName}
                    </Option>
                  );
                }
              ),
            ]}
          </WrapperSelect>
        </OverlayInputWrapper>
      </Form.Item>
    </>
  );
};
